import { MdArrowDropDown, MdSend } from 'react-icons/md';

import { Button } from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import MailDialog from './MailDialog';
import MailMessageView from './MailMessageView';
import { recipientLabel, type CandidateRecipient } from '../lib/recipients';
import { mailMenuContentClassName, mailMenuItemClassName } from './VariableInsertMenu';

type CandidateMailPreviewModalProps = {
  open: boolean;
  onClose: () => void;
  senderName?: string | null;
  senderEmail?: string | null;
  signature?: string | null;
  recipients: CandidateRecipient[];
  subject: string;
  body: string;
  saveAsTemplate: boolean;
  onSaveAsTemplateChange: (next: boolean) => void;
  onSendNow: () => void;
  onSchedule: () => void;
  isSending?: boolean;
};

export default function CandidateMailPreviewModal({
  open,
  onClose,
  senderName,
  senderEmail,
  signature,
  recipients,
  subject,
  body,
  saveAsTemplate,
  onSaveAsTemplateChange,
  onSendNow,
  onSchedule,
  isSending = false,
}: CandidateMailPreviewModalProps) {
  const canSend =
    recipients.length > 0 && subject.trim().length > 0 && body.trim().length > 0 && !isSending;
  const trimmedSignature = signature?.trim() ?? '';
  const bodyWithSignature = trimmedSignature.length > 0 ? `${body}\n\n${trimmedSignature}` : body;

  return (
    <MailDialog
      open={open}
      onClose={onClose}
      title="プレビュー"
      widthClassName="w-[860px]"
      bodyClassName="px-lg py-md"
      preventClose={isSending}
      footerStart={
        <Checkbox checked={saveAsTemplate} onChange={onSaveAsTemplateChange} disabled={isSending}>
          <span className="text-body-sm text-main">テンプレートとして保存</span>
        </Checkbox>
      }
      footer={
        <>
          <Button type="button" color="secondary" size="md" disabled={isSending} onClick={onClose}>
            キャンセル
          </Button>
          <div className="flex items-stretch">
            <Button
              type="button"
              color="primary"
              size="md"
              className="rounded-r-none! [border-right:1px_solid_var(--color-primary_darken)]!"
              disabled={!canSend}
              onClick={onSendNow}
            >
              <MdSend className="size-5" />
              今すぐ送信
            </Button>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  color="primary"
                  size="md"
                  iconOnly
                  aria-label="送信方法を選択"
                  disabled={!canSend}
                  className="w-10! rounded-l-none! [&[data-state=open]>svg]:rotate-180"
                >
                  <MdArrowDropDown className="size-5 transition-transform" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                align="end"
                sideOffset={4}
                className={`w-[164px] ${mailMenuContentClassName}`}
              >
                <DropdownMenuItem onClick={onSendNow} className={mailMenuItemClassName}>
                  今すぐ送信
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onSchedule} className={mailMenuItemClassName}>
                  送信日時を設定
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </>
      }
    >
      <MailMessageView
        recipientSummary={recipients.map((recipient) => recipient.name).join(', ')}
        recipientItems={recipients.map((recipient) => ({
          key: recipient.id,
          label: recipientLabel(recipient),
        }))}
        subject={subject}
        senderName={senderName ?? ''}
        senderEmail={senderEmail ?? ''}
        body={bodyWithSignature}
      />
    </MailDialog>
  );
}
